import {
  Directive,
  ElementRef,
  Renderer2,
  inject,
  effect,
} from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { PRIMARY_OUTLET, Route, Router, RouterLink } from "@angular/router";
import { SharedService } from "src/app/_shared/shared.service";
import { AccessGuard } from "src/app/SayTS/access-guard";

/**
 * Removes any host that also has `[routerLink]` from the DOM when the current
 * user couldn't actually activate that route — e.g. a nav item pointing at a
 * route guarded by `AccessGuard`. Keeps menus/links from advertising pages the
 * click would just bounce off of.
 */
@Directive({
  selector: "[routerLink]",
})
export class RouterLinkAccessDirective {
  private routerLink = inject(RouterLink, { self: true, optional: true });
  private router = inject(Router);
  private el = inject(ElementRef<HTMLElement>);
  private renderer = inject(Renderer2);
  private shared = inject(SharedService);
  private accessGuard = inject(AccessGuard);

  /** Where the host used to sit — kept so it can be reinserted if access is regained. */
  private parent: Node | null = null;
  private nextSibling: Node | null = null;
  private removed = false;

  constructor() {
    // rightsAll arrives asynchronously after login — re-evaluate once it does.
    const rights = toSignal(this.shared.rightsAllWatch);

    effect(() => {
      rights();
      this.applyVisibility();
    });
  }

  private applyVisibility(): void {
    if (!this.routerLink) return;

    const urlTree = this.routerLink.urlTree;
    if (!urlTree) return;

    const segments = (
      urlTree.root.children[PRIMARY_OUTLET]?.segments ?? []
    ).map((s) => s.path);

    const route = this.findMatchingRoute(segments);
    const allowed =
      !route || this.accessGuard.canAccessSilently(route.data ?? {});

    if (allowed) {
      this.restore();
    } else {
      this.remove();
    }
  }

  private remove(): void {
    if (this.removed) return;
    const nativeEl = this.el.nativeElement;
    const parent = this.renderer.parentNode(nativeEl);
    if (!parent) return;
    this.parent = parent;
    this.nextSibling = this.renderer.nextSibling(nativeEl);
    this.renderer.removeChild(parent, nativeEl);
    this.removed = true;
  }

  private restore(): void {
    if (!this.removed || !this.parent) return;
    this.renderer.insertBefore(
      this.parent,
      this.el.nativeElement,
      this.nextSibling,
    );
    this.removed = false;
  }

  private findMatchingRoute(segments: string[]): Route | undefined {
    return this.router.config.find((r) => this.pathMatches(r.path, segments));
  }

  private pathMatches(path: string | undefined, segments: string[]): boolean {
    if (path == null) return false;
    const templateSegments = path.split("/").filter(Boolean);
    if (templateSegments.length !== segments.length) return false;
    return templateSegments.every(
      (t, i) => t.startsWith(":") || t === segments[i],
    );
  }
}
